import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import { GetApp } from "@material-ui/icons";
import { CSVLink } from "react-csv";

const LEFT_COL_PX = 200; // 先頭列の固定幅
const COL_MIN_PX  = 90;  // 各日付列の最小幅

const useStyles = makeStyles({
 headerRow: {
   display: "flex",
   alignItems: "center",
   justifyContent: "flex-end", // ← 右寄せ
   padding: 16,
 },
  container: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    minWidth: 560, // 横幅が狭い時に横スクロール
  },
  th: {
    border: "1px solid #eee6e6",
    padding: "8px 10px",
    textAlign: "right",
    fontSize: 11,
    whiteSpace: "nowrap",
    minWidth: 80,
  },
  thSticky: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    textAlign: "left",
    background: "#fff",
    minWidth: 200,
  },
  td: {
    border: "1px solid #eee6e6",
    padding: "8px 10px",
    textAlign: "right",
    fontSize: 13,
    height: 45,
  },
  tdSticky: {
    position: "sticky",
    left: 0,
    zIndex: 1,
    textAlign: "left",
    background: "#fff",
    fontSize: 13,
    minWidth: 200,
  },
  exportBtn: { marginLeft:16 },
});

const PicTreeTablePhotoSubType = ({ trendData, orderKeys, chartSize = "day", loading }) => {
  const classes = useStyles();
  const trend = Array.isArray(trendData) && trendData.length > 0 ? trendData[0] : null;

  const seriesKeys = React.useMemo(() => {
    if (!trend) return [];
    const keys = Object.keys(trend).filter((k) => k !== "date");
    if (Array.isArray(orderKeys) && orderKeys.length) return orderKeys.filter((k) => keys.includes(k));
    return keys
      .map((k) => ({
        k,
        sum: (trend[k] || []).reduce(
          (acc, v) => acc + (Number(v) || 0),
          0
        )
      }))
      .sort((a,b)=>b.sum-a.sum)
      .map(o=>o.k);
  }, [trend, orderKeys]);

  // ---- CSV生成（動的）
  const getExportData = React.useCallback(() => {
    if (!trend) return [];
    const header = ["アセット別撮影データ", ...(trend.date || []).map((d) => (chartSize !== "hour" ? moment(d).format("M/D") : moment(d).format("M/D HH:mm")))];
    const rows = seriesKeys.map((k) => [k, ...(trend[k] || [])]); // ラベルはキー名（日本語）をそのまま
    return [header, ...rows];
  }, [trend, seriesKeys, chartSize]);

  if (!trend) return null;

  const dates = trend.date || [];
  const minTableWidth = LEFT_COL_PX + dates.length * COL_MIN_PX;

  return (
    <Paper>
      <div>
        {/* <div className={classes.widgetTitle}></div> */}
        <div className={classes.headerRow}>
          <CSVLink data={getExportData()} filename={`PicTree-アセット別撮影トレンド.csv`}>
            <Button variant="contained" color="default" className={classes.exportBtn} startIcon={<GetApp />}>
              CSV出力
            </Button>
          </CSVLink>
        </div>
      </div>

      <div className={classes.container}>
        <table className={classes.table} style={{ minWidth: minTableWidth }}>
          <thead>
            <tr>
              <th className={`${classes.th} ${classes.thSticky}`} style={{textAlign:"left"}}></th>
              {dates.map((d,i)=>(
                <th key={i} className={classes.th}>
                  {chartSize !== "hour" ? moment(d).format("M/D") : moment(d).format("M/D HH:mm")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {seriesKeys.map((k)=>(
              <tr key={k}>
                <td className={`${classes.td} ${classes.tdSticky}`}>{k}</td>
                {(trend[k] || []).map((v,i)=>(
                  <td key={`${k}-${i}`} className={classes.td}>{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

PicTreeTablePhotoSubType.propTypes = {
  trendData: PropTypes.array.isRequired, // [{ date:[], "<日本語ラベル>":[] , ... }]
  orderKeys: PropTypes.array,            // ["通常電柱","ツルツタ電柱"] など（任意）
  chartSize: PropTypes.oneOf(["hour", "day"]),
  loading: PropTypes.bool,
};

export default PicTreeTablePhotoSubType;
